"use client";

import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";

const LIGHT_SECTION_IDS = [
  "section-help",
  "section-products",
  "how-it-works",
  "work-at-fast",
  "partnerships",
];

const FILTER_WHITE = "brightness(0) invert(1)";
const FILTER_SECONDARY = "fast-secondary-contrast(100%)";

export default function FixedLogo() {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const [whiteClip, setWhiteClip] = useState("none");
  const [secClip, setSecClip] = useState("inset(0 0 100% 0)");

  useEffect(() => {
    function update() {
      const anchor = anchorRef.current;
      if (!anchor) return;

      // Get actual rendered position of the logo element
      const logoRect = anchor.getBoundingClientRect();
      const LOGO_TOP = logoRect.top;
      const LOGO_H = logoRect.height;
      const LOGO_BOT = LOGO_TOP + LOGO_H;

      // Guard against zero-height (not yet rendered)
      if (LOGO_H === 0) return;

      let lightTop = LOGO_BOT;
      let lightBot = LOGO_TOP;
      let hasLight = false;

      for (const id of LIGHT_SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const oTop = Math.max(r.top, LOGO_TOP);
        const oBot = Math.min(r.bottom, LOGO_BOT);
        if (oTop < oBot) {
          hasLight = true;
          if (oTop < lightTop) lightTop = oTop;
          if (oBot > lightBot) lightBot = oBot;
        }
      }

      if (!hasLight) {
        setWhiteClip("none");
        setSecClip("inset(0 0 100% 0)");
        return;
      }

      const tPct = +((lightTop - LOGO_TOP) / LOGO_H * 100).toFixed(2);
      const bPct = +((LOGO_BOT - lightBot) / LOGO_H * 100).toFixed(2);

      if (tPct <= 0 && bPct <= 0) {
        setWhiteClip("inset(0 0 100% 0)");
        setSecClip("none");
        return;
      }

      setSecClip(`inset(${tPct}% 0 ${bPct}% 0)`);
      if (tPct > 0) {
        setWhiteClip(`inset(0 0 ${100 - tPct}% 0)`);
      } else {
        setWhiteClip(`inset(${100 - bPct}% 0 0 0)`);
      }
    }

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true }); // recalc on resize too
    update();
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
  
    <Link
      ref={anchorRef}
      href="/"
      className="hidden md:block fixed top-5 left-6 md:left-12 z-[60]"
      style={{ width: 120, height: 60, transform: "translateZ(0)", willChange: "transform" }}
    >
      {/* White version — shown over dark backgrounds */}
      <span
        style={{
          position: "absolute", inset: 0, display: "block",
          clipPath: whiteClip,
          transform: "translateZ(0)",
          willChange: "clip-path",
        }}
      >
        <Image
          src="/fast-logo.svg"
          alt="Fast App"
          width={120}
          height={60}
          priority
          className="w-[80px] xl:w-[120px] h-auto"
          style={{ filter: FILTER_WHITE, display: "block" }}
        />
      </span>
      {/* Dark-green version — shown over light backgrounds */}
      <span
        style={{
          position: "absolute", inset: 0, display: "block",
          clipPath: secClip,
          transform: "translateZ(0)",
          willChange: "clip-path",
        }}
      >
        <Image
          src="/fast-logo.svg"
          alt=""
          aria-hidden
          width={120}
          height={60}
          className="w-[80px] xl:w-[120px] h-auto"
          style={{ filter: FILTER_SECONDARY, display: "block" }}
        />
      </span>
    </Link>
  );
}